import React, { useState, useEffect } from 'react';
import { Card, Table, DatePicker, Button, Space, Typography, message, Spin, Empty, Grid } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { unsignedDocumentService, UnsignedDocument } from '../services/unsignedDocumentService';

const { RangePicker } = DatePicker;
const { Title } = Typography;
const { useBreakpoint } = Grid;

const CheckSignTab: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [signingMakcb, setSigningMakcb] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('day'),
    dayjs().endOf('day'),
  ]);
  const [data, setData] = useState<UnsignedDocument[]>([]);
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    checkPermission();
    // Auto-fetch data when component mounts and user has permission
    if (hasPermission) {
      fetchData();
    }
  }, [hasPermission]);

  const checkPermission = () => {
    try {
      const permissions = localStorage.getItem('permissions');
      console.log('Raw permissions from localStorage:', permissions);
      
      if (!permissions) {
        console.log('No permissions found in localStorage');
        setHasPermission(false);
        return;
      }

      try {
        const parsedPermissions = JSON.parse(permissions);
        console.log('Parsed permissions:', parsedPermissions);
        
        if (Array.isArray(parsedPermissions)) {
          const hasCheckSignPermission = parsedPermissions.includes('CheckSign');
          console.log('Has CheckSign permission:', hasCheckSignPermission);
          setHasPermission(hasCheckSignPermission);
        } else {
          console.log('Permissions is not an array');
          setHasPermission(false);
        }
      } catch (e) {
        console.error('Error parsing permissions:', e);
        setHasPermission(false);
      }
    } catch (error) {
      console.error('Error checking permissions:', error);
      setHasPermission(false);
    }
  };

  const fetchData = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.warning('Vui lòng chọn khoảng thời gian');
      return;
    }

    setLoading(true);
    try {
      // The token is now automatically handled by the axios interceptor.
      // No need to manually get it from localStorage.
      const [fromDate, toDate] = dateRange;
      const result = await unsignedDocumentService.getUnsignedDocuments(fromDate, toDate);
      
      setData(result);
      if (result.length > 0) {
        message.success(`Đã tìm thấy ${result.length} bản ghi`);
      }
    } catch (error) {
      console.error('Error in fetchData:', error);
      message.error('Đã xảy ra lỗi khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  const handleTransferSignature = async (makcb: string) => {
    setSigningMakcb(makcb);
    try {
        await unsignedDocumentService.transferPatientSignature(makcb);
        message.success('Chuyển chữ ký thành công!');
        fetchData(); // Refresh data to update the list
    } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Lỗi khi chuyển chữ ký';
        message.error(errorMessage);
    } finally {
        setSigningMakcb(null);
    }
  };

  const columns = [
    {
      title: 'Mã KCB',
      dataIndex: 'MaKCB',
      key: 'MaKCB',
    },
    {
      title: 'Họ tên',
      dataIndex: 'HoTen',
      key: 'HoTen',
    },
    {
      title: 'Khoa khám',
      dataIndex: 'TenKK',
      key: 'TenKK',
    },
    {
      title: 'Nhân viên',
      dataIndex: 'TenNV',
      key: 'TenNV',
    },
    {
      title: 'Ngày thanh toán',
      dataIndex: 'NgayTT',
      key: 'NgayTT',
      render: (date: string) => unsignedDocumentService.formatDate(date),
    },
    {
      title: 'Thiếu chữ ký',
      dataIndex: 'ThieuChuKy',
      key: 'ThieuChuKy',
      render: (text: string) => text || 'N/A',
    },
    {
      title: 'Hành động',
      key: 'action',
      render: (_: any, record: UnsignedDocument) => {
        if (record.ThieuChuKy.includes('Bệnh nhân')) {
          return (
            <Button
              onClick={() => handleTransferSignature(record.MaKCB)}
              loading={signingMakcb === record.MaKCB}
              size="small"
            >
              Chuyển chữ ký
            </Button>
          );
        }
        return null;
      },
    },
  ];

  if (!hasPermission) {
    return (
      <div style={{ textAlign: 'center', marginTop: '20%' }}>
        <Title level={4}>Bạn không có quyền truy cập chức năng này</Title>
      </div>
    );
  }

  return (
    <div className="check-sign-tab">
      {isMobile ? (
        <div style={{ padding: '12px' }}>
          <Card 
            style={{ marginBottom: 16, borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
            bodyStyle={{ padding: '16px' }}
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div style={{ fontSize: '15px', fontWeight: 500, color: 'rgba(0, 0, 0, 0.85)' }}>
                Chọn khoảng thời gian
              </div>
              <RangePicker
                value={dateRange as any}
                onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs])}
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
                popupStyle={{ width: 'calc(100vw - 48px)' }}
                inputReadOnly={true}
                size="large"
                allowClear={false}
                disabledDate={(current) => current && current > dayjs().endOf('day')}
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={fetchData}
                loading={loading}
                style={{ width: '100%', height: '44px', borderRadius: '6px' }}
                size="large"
              >
                Lấy dữ liệu
              </Button>
            </Space>
          </Card>
          <Card 
            style={{ marginTop: 16, borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
            bodyStyle={{ padding: '16px' }}
          >
            <Table
              columns={columns}
              dataSource={data}
              rowKey="MaKCB"
              loading={loading}
              locale={{
                emptyText: (
                  <Empty
                    description={
                      loading ? 'Đang tải dữ liệu...' : 'Chưa có dữ liệu hoặc chưa tìm kiếm'
                    }
                  />
                ),
              }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total) => `Tổng cộng: ${total} bản ghi`,
              }}
              scroll={{ x: 'max-content' }}
              size="small"
            />
          </Card>
        </div>
      ) : (
        <Card 
          title="Danh sách đã thanh toán chưa ký số bảng kê" 
          bordered={false}
          extra={
            <Space direction="horizontal" style={{ width: 'auto' }}>
              <RangePicker
                value={dateRange as any}
                onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs])}
                format="DD/MM/YYYY"
                style={{ width: 300 }}
                disabledDate={(current) => current && current > dayjs().endOf('day')}
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={fetchData}
                loading={loading}
              >
                Lấy dữ liệu
              </Button>
            </Space>
          }
        >
          <Table
            columns={columns}
            dataSource={data}
            rowKey="MaKCB"
            loading={loading}
            locale={{
              emptyText: (
                <Empty
                  description={
                    loading ? 'Đang tải dữ liệu...' : 'Chưa có dữ liệu hoặc chưa tìm kiếm'
                  }
                />
              ),
            }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total) => `Tổng cộng: ${total} bản ghi`,
            }}
            scroll={{ x: 'max-content' }}
          />
        </Card>
      )}
    </div>
  );
};

export default CheckSignTab;